/**
 * @module app/App
 * @description Shell: site header, route switch and footer.
 * The ballroom screen renders chrome-free for projection.
 */
import { useEffect } from "react";
import { hrefFor, useRoute } from "./router";
import { editionSource, useEdition } from "./edition";
import { HomePage } from "../pages/HomePage";
import { CardPage } from "../pages/CardPage";
import { PledgePage } from "../pages/PledgePage";
import { ScreenPage } from "../pages/ScreenPage";
import { AdminPage } from "../pages/AdminPage";
import { TermsPage } from "../pages/TermsPage";
import { PrintStudioPage } from "../pages/PrintStudioPage";
import { ThankYouPage } from "../pages/ThankYouPage";
import { CAMPAIGN } from "../config/campaign";
import { EDITIONS } from "../config/editions";
import { useSettings } from "../data";
import { BrandLockup } from "../components/Brand";

/** Shown only when an edition was chosen by link (?edition=…), so a reviewer always knows which edition they are looking at. */
function EditionRibbon() {
    const edition = useEdition();
    const { editionOverride } = useSettings();
    if (editionSource(editionOverride) !== "url") return null;
    const other = edition.id === "gala" ? EDITIONS.campaign : EDITIONS.gala;
    return (
        <p className="edition-ribbon" role="note">
            Previewing the <strong>{edition.label}</strong> edition ·{" "}
            <a href={`?edition=${other.id}${window.location.hash}`}>switch to {other.label}</a>
        </p>
    );
}

export function App() {
    const route = useRoute();
    const edition = useEdition();

    // Route changes are page changes: return scroll to the top.
    // // BUG FIX: the effect body must be a block, not an expression.
    // What caused this: current Chrome returns a Promise from window.scrollTo(); an
    // expression-bodied arrow handed that Promise back to React as the effect's
    // "cleanup", and React crashed ("p is not a function") on the next route change,
    // so nav links changed the URL but the page never re-rendered.
    // Why chosen: an effect should return nothing or a cleanup function, never a value.
    // Side-effects: none.
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [route.name]);

    if (route.name === "screen") return <ScreenPage />;

    return (
        <div className="shell">
            <EditionRibbon />
            <header className="site-head">
                <a href={hrefFor({ name: "home" })} className="site-brand" aria-label={`${CAMPAIGN.campaignName} — home`}>
                    <BrandLockup variant="horizontal" />
                </a>
                <nav className="site-nav" aria-label="Primary">
                    <a href={hrefFor({ name: "home" })} aria-current={route.name === "home" ? "page" : undefined}>Stories</a>
                    {edition.showBallroomScreen && <a href={hrefFor({ name: "screen" })}>Ballroom screen</a>}
                    <a href={hrefFor({ name: "terms" })} aria-current={route.name === "terms" ? "page" : undefined}>Terms &amp; Conditions</a>
                    <a href={hrefFor({ name: "admin" })} aria-current={route.name === "admin" || route.name === "print" ? "page" : undefined}>Admin</a>
                    <a href={hrefFor({ name: "pledge", fromSlug: null })} className="btn btn--primary">{edition.nav.giveLabel}</a>
                </nav>
            </header>

            <main id="main">
                {route.name === "home" && <HomePage />}
                {route.name === "card" && <CardPage slug={route.slug} />}
                {route.name === "pledge" && <PledgePage fromSlug={route.fromSlug} />}
                {route.name === "admin" && <AdminPage />}
                {route.name === "terms" && <TermsPage />}
                {route.name === "print" && <PrintStudioPage />}
                {route.name === "thank-you" && <ThankYouPage slug={route.slug} />}
            </main>

            <footer className="site-foot">
                <p className="pc-script">{CAMPAIGN.campaignName}.</p>
                <p>
                    AFJ is a 501(c)(3) nonprofit, EIN {CAMPAIGN.ein}. Gifts are tax-deductible to the extent allowed by law.
                </p>
                <p>{CAMPAIGN.mailingAddress} · {CAMPAIGN.phone}</p>
                <p><a href={hrefFor({ name: "terms" })}>Terms and Conditions</a></p>
            </footer>
        </div>
    );
}
